/**
 * The translate-it stamp of a learner attempt (plan 47 § The stamp): the
 * screen is the referee, the coach's voice stays neutral. Which stamp a bubble
 * gets is decided by `bubbleGameStamp` / `gameTurnStampOf` (the same data as its
 * correction chips, so they always agree); this only renders it.
 *
 * Fixed colours for every game (the game's own hue lives only in the arena
 * tint and the score pill): perfect / good = lime, close = amber (the
 * GRAMÁTICA chip's), miss = coral. Always icon + text, never colour alone.
 */
import type { ReactElement } from 'react';
import type { GameStamp } from '@orborix/shared-types';
import type { GameBubbleStamp } from '../gameArena.js';
import type { GameAttemptControl } from '../hooks/useGameArena.js';
import '../gameArena.css';

/** Learner-facing copy (Spanish, speak's UI language). */
export const GAME_STAMP_COPY: Record<GameStamp, { label: string; spoken: string }> = {
  perfect: { label: '¡Perfecto!', spoken: 'Intento perfecto' },
  good: { label: '¡Bien!', spoken: 'Intento bien, con un detalle' },
  close: { label: 'Casi', spoken: 'Intento casi: tiene un error' },
  miss: { label: 'No era eso', spoken: 'Intento: no era eso' },
};

export const GAME_STAMP_JUDGING_LABEL = 'Juzgando';

interface GameStampBadgeProps {
  stamp: GameBubbleStamp;
  /**
   * A live bubble: the stamp slot is a polite live region, so the judging →
   * stamp change is announced. Off in the history (nothing changes there).
   */
  live?: boolean;
}

export function GameStampBadge({ stamp, live = false }: GameStampBadgeProps): ReactElement {
  // The slot stays the same node from judging to stamped: a live region only
  // announces changes INSIDE an element that was already there.
  const liveProps = live ? ({ role: 'status', 'aria-live': 'polite' } as const) : {};
  if (stamp === 'judging') {
    return (
      <span className="game-stamp-slot" {...liveProps}>
        <span className="game-stamp is-judging">
          <span className="game-stamp__text">{GAME_STAMP_JUDGING_LABEL}</span>
          <span className="conv-typing-dots game-stamp__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </span>
      </span>
    );
  }
  const copy = GAME_STAMP_COPY[stamp];
  return (
    <span className="game-stamp-slot" {...liveProps}>
      <span
        className={`game-stamp is-${stamp}`}
        role="img"
        aria-label={copy.spoken}
        title={copy.label}
      >
        <StampIcon stamp={stamp} />
        <span className="game-stamp__text" aria-hidden="true">
          {copy.label}
        </span>
      </span>
    </span>
  );
}

function StampIcon({ stamp }: { stamp: GameStamp }): ReactElement {
  const common = {
    viewBox: '0 0 24 24',
    width: 14,
    height: 14,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 3,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: 'game-stamp__icon',
  };
  switch (stamp) {
    case 'perfect':
      return (
        <svg {...common}>
          <path d="M3.5 12.5 L 9 18 L 20.5 5.5" />
          <path d="M19 15.5 v 5 M16.5 18 h 5" strokeWidth={2} />
        </svg>
      );
    case 'good':
      return (
        <svg {...common}>
          <path d="M4 12.5 L 9.5 18 L 20 6" />
        </svg>
      );
    case 'close':
      return (
        <svg {...common}>
          <path d="M3.5 13.5 C 6 9, 9 9, 12 12 S 18 15, 20.5 10.5" />
        </svg>
      );
    case 'miss':
      return (
        <svg {...common}>
          <path d="M6 6 L 18 18 M 18 6 L 6 18" />
        </svg>
      );
  }
}

/** Learner-facing copy of the attempt controls. */
export const GAME_ATTEMPT_ACTION_COPY = {
  retry: { label: '↺ Repetirla', used: '↺ Va de nuevo', aria: 'Repetir esta frase' },
  skip: { label: 'Saltear →', used: 'Salteada', aria: 'Saltear esta frase' },
} as const;

interface GameAttemptActionButtonProps {
  control: GameAttemptControl;
  onRun: () => void;
}

/**
 * The subtle ghost control under the NEWEST attempt (plan 47 § Retry and
 * skip): «↺ Repetirla» on a close, «Saltear →» on a first-try miss. Disabled
 * while the coach is generating and after use (it then says it was asked).
 */
export function GameAttemptActionButton({
  control,
  onRun,
}: GameAttemptActionButtonProps): ReactElement {
  const copy = GAME_ATTEMPT_ACTION_COPY[control.action];
  return (
    <button
      type="button"
      className={`game-attempt-action is-${control.action}${control.used ? ' is-used' : ''}`}
      disabled={control.disabled}
      onClick={onRun}
      aria-label={copy.aria}
    >
      {control.used ? copy.used : copy.label}
    </button>
  );
}
